import { GuidePriority, type BusyReason, type GuideContext, type GuideSignal, type GuideStep } from "./guide";
import { HUB_FIRST_SESSION } from "./guideSave";

/**
 * The guide queue's vocabulary (guideQueue.ts re-exports it) and which steps may show (DESIGN §4.2): the switches
 * that hide a step, the hub's first session, and the ranked candidates for a screen. Pure functions of the queue's
 * steps, done set, switches, the context and the host's state; the queue owns that state and passes it in.
 */

export type EndReason = "until" | "tap" | "cta" | "alt" | "close" | "timeout" | "withdrawn" | "suppressed" | "yield" | "replaced";

export type QueueEvent =
  | { readonly type: "show"; readonly step: GuideStep }
  | { readonly type: "end"; readonly step: GuideStep; readonly reason: EndReason; readonly done: boolean }
  | { readonly type: "aside"; readonly step: GuideStep }
  | { readonly type: "back"; readonly step: GuideStep };

/** One evaluation's view of the active step, for the overlay. */
export interface ActiveView {
  readonly step: GuideStep;
  readonly text: string;
  /** Stepped aside for something busy: hidden, clock frozen. */
  readonly aside: boolean;
  /** An info step's "Tap to continue" cue is due. */
  readonly cue: boolean;
  /** A tip's time left, 1 → 0; null for other kinds. */
  readonly drain: number | null;
  /** Shown by the debug hook: ignores `when`, `until` and done bookkeeping. */
  readonly forced: boolean;
}

/** The host's state at this evaluation. */
export interface QueueEnv {
  readonly busy: BusyReason | null;
  /** The page is visible (document.visibilityState). */
  readonly visible: boolean;
  /** A `needsTarget` step's target is on screen now (the controller resolves it); absent reads as on screen. */
  readonly ready?: (step: GuideStep) => boolean;
  /**
   * M45: nothing covers the step's target or its screen (guideDom.ts surfaceClear: the screen is the topmost
   * interactive layer and the target is hittable). A step starts only while this holds and steps aside while it does
   * not; absent reads as clear.
   */
  readonly clear?: (step: GuideStep) => boolean;
}

/** A step waiting to show: since when its `when` has held, and the signals latched for it meanwhile. */
export interface Queued { readonly since: number; readonly latched: Set<GuideSignal> }

/**
 * What busy reasons do to a step already on screen (owner 2026-09-30: "hints are flashing on/off the screen"). A wave
 * banner, a moment callout or the enemy card come and go every few seconds in a battle; a step that stepped aside for
 * each of them blinked off and back on after the quiet grace. They now only hold back a step's *start*: once up, a step
 * stays through them. Only what truly takes the screen (a sheet, the pause layer, the intro card, a dialog, a load, the
 * cloud wipe) puts a step aside.
 */
export const PASSING_BUSY: ReadonlySet<BusyReason> = new Set<BusyReason>(["banner", "callout", "tooltip"]);
/** This busy reason puts a step on screen aside (PASSING_BUSY does not). */
export function hidesShown(busy: BusyReason | null): boolean { return busy !== null && !PASSING_BUSY.has(busy); }

/** The two player switches that hide steps. */
export interface GuideSwitches { readonly tipsOff: boolean; readonly walkthroughSkipped: boolean }

/** The walkthrough and the hub's first session go with "Skip tips"; the welcome card's alt link too. */
export function isWalkthrough(step: GuideStep): boolean { return step.walkthrough === true || (HUB_FIRST_SESSION as readonly string[]).includes(step.id); }

/**
 * A warning about a real threat (the first boss, flyers, bombers, the fortress in danger; priority Safety). Neither
 * the tips switch nor "Skip tips" silences one (owner, 2026-09-24): each still shows once per save.
 */
export function isSafety(step: GuideStep): boolean { return step.priority === GuidePriority.Safety; }

/** DESIGN §4.2.1: skipping hides the walkthrough; tips off hides every tip and every in-battle hint but the warnings. */
export function suppressed(step: GuideStep, sw: GuideSwitches): boolean {
  if (sw.walkthroughSkipped && isWalkthrough(step)) return true;
  if (!sw.tipsOff || isSafety(step)) return false;
  return step.kind === "tip" || (step.screen === "level" && step.walkthrough !== true);
}

/**
 * The hub's first session (hub.firstWin … hub.play2) is still running: registered, not finished, not skipped, and not
 * outgrown (two levels cleared, however its steps went).
 */
export function firstSessionOpen(registered: (id: string) => boolean, done: ReadonlySet<string>, walkthroughSkipped: boolean, highestCleared: number): boolean {
  const last = HUB_FIRST_SESSION[HUB_FIRST_SESSION.length - 1];
  return registered(last) && !done.has(last) && !walkthroughSkipped && highestCleared < 2;
}

/** A `needsTarget` step's target is on screen (or the step needs none). */
export function targetReady(step: GuideStep, env: QueueEnv): boolean {
  if (step.needsTarget !== true || !env.ready) return true;
  try { return env.ready(step); } catch { return false; }
}

/**
 * A script's `when` or `until` on this context, guarded: a script bug must not break the game, so a condition that
 * throws reads as false (the step just never qualifies) and is reported once per step and condition (`warned`).
 */
export function guarded(step: GuideStep, what: "when" | "until", f: (c: GuideContext) => boolean, ctx: GuideContext, warned: Set<string>): boolean {
  try { return f(ctx) === true; } catch (e) {
    const key = `${step.id}.${what}`;
    if (!warned.has(key)) { warned.add(key); console.warn(`[guide] ${key} threw`, e); }
    return false;
  }
}

/** A step's words for this context ("" without one, or when its text function throws). */
export function stepText(step: GuideStep, ctx: GuideContext | null): string {
  if (typeof step.text === "string") return step.text;
  if (!ctx) return "";
  try { return step.text(ctx); } catch { return ""; }
}

/** What `candidates` reads: the queue's steps and state at this evaluation. */
export interface SelectState {
  readonly steps: readonly GuideStep[];
  /** The step on screen, which is never its own candidate. */
  readonly active: GuideStep | null;
  readonly done: ReadonlySet<string>;
  readonly switches: GuideSwitches;
  /** A `onePerVisit` step has shown since the screen last changed. */
  readonly visitSpent: boolean;
  readonly now: number;
  /** The step's `when` for this context (guarded: a script that throws reads as false). */
  readonly when: (step: GuideStep, ctx: GuideContext) => boolean;
}

/** The queue's bookkeeping that `candidates` keeps up to date: the waiting steps and the ones dropped by maxWaitMs. */
export interface SelectBook { readonly queued: Map<string, Queued>; readonly dropped: Set<string> }

/** The candidates for `ctx.screen`, best first; keeps the queued map, latches and drops up to date. */
export function candidates(s: SelectState, book: SelectBook, ctx: GuideContext, env: QueueEnv): GuideStep[] {
  const t = s.now;
  const out: GuideStep[] = [];
  const afterDone = (step: GuideStep) => (step.after ?? []).every((id) => s.done.has(id));
  const alreadySaid = (step: GuideStep) => (step.unless ?? []).some((id) => s.done.has(id));
  for (const step of s.steps) {
    if (step.screen !== ctx.screen) continue;
    if (s.active === step) continue;
    if (s.done.has(step.id) || suppressed(step, s.switches) || !afterDone(step) || alreadySaid(step) || (step.onePerVisit === true && s.visitSpent)) { book.queued.delete(step.id); continue; }
    const q = book.queued.get(step.id);
    const signals = q && q.latched.size > 0 ? new Set([...ctx.signals, ...q.latched]) : ctx.signals;
    if (!s.when(step, { ...ctx, signals }) || !targetReady(step, env)) { book.queued.delete(step.id); book.dropped.delete(step.id); continue; }
    if (book.dropped.has(step.id)) continue;
    if (!q) book.queued.set(step.id, { since: t, latched: new Set(ctx.signals) });
    else for (const sig of ctx.signals) q.latched.add(sig);
    const since = q?.since ?? t;
    if (step.maxWaitMs !== undefined && t - since > step.maxWaitMs) { book.queued.delete(step.id); book.dropped.add(step.id); continue; }
    out.push(step);
  }
  // Stable: equal priorities keep registration order.
  return out.sort((a, b) => (b.priority ?? GuidePriority.Tip) - (a.priority ?? GuidePriority.Tip));
}
